import React, { useState } from "react"
import ReactS3Uploader from "react-s3-uploader"
import { UPLOADED, UPLOADING } from "../enums/FileStates"
import { Progress } from "antd"
import { gql, useMutation } from "@apollo/client"
import { useAuth0 } from "@auth0/auth0-react"
import {
  asyncLoadImageFromFile,
  asyncLoadVideoFromFile,
  uiHandleError,
} from "../utils"

const INSERT_FILE_MUTATION = gql`
  mutation insertFile($data: files_insert_input!) {
    insert_files_one(object: $data) {
      id
    }
  }
`

const FileUploader = ({
  accept,
  onError,
  onSuccess,
  showInput = true,
  showProgress = true,
  isPublic = true,
}) => {
  const [progress, setProgress] = useState(0)
  const [fileState, setFileState] = useState(null)
  const [inputRef, setInputRef] = useState(null)
  const [insertFile, { loading, error, data }] =
    useMutation(INSERT_FILE_MUTATION)
  const { getAccessTokenSilently } = useAuth0()

  let currentFile = null
  let currentFileMeta = {}

  const getSignedUrl = async (file, callback) => {
    try {
      currentFile = new File([file], file.name)
      const { name, type, size } = file
      currentFileMeta = {
        filename: name,
        extension: name.split(".").pop(),
        mimetype: type,
        size,
      }

      switch (type.split("/")[0]) {
        case "video":
          const tempVideo = await asyncLoadVideoFromFile(file)
          currentFileMeta = {
            ...currentFileMeta,
            width: tempVideo.videoWidth,
            height: tempVideo.videoHeight,
            duration: tempVideo.duration,
          }
          break
        case "audio":
          break
        case "image":
          const tempImg = await asyncLoadImageFromFile(file)
          currentFileMeta = {
            ...currentFileMeta,
            width: tempImg.width,
            height: tempImg.height,
          }
          break
      }

      setFileState(UPLOADING)
      const token = await getAccessTokenSilently()

      const config = {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }

      const url = `/.netlify/functions/get-upload-url`

      const response = await fetch(url, config)

      const json = await response.json()

      callback(json)
    } catch (error) {
      uiHandleError({ error })
      console.error(error)
    }
  }

  const onProgress = progress => {
    setProgress(progress)
  }

  const _onSuccess = async ({ key, signedUrl }) => {
    setFileState(UPLOADED)
    inputRef.value = null

    try {
      const data = {
        ...currentFileMeta,
        s3_key: key.replace(/.+\//, ""),
        is_public: isPublic,
      }
      const result = await insertFile({ variables: { data } })

      onSuccess &&
        onSuccess({ key, signedUrl, file_id: result.data.insert_files_one.id })
    } catch (e) {
      uiHandleError({
        error: e,
        action: () => {},
      })
      onError && onError(e)
    }
  }

  return (
    <div>
      <div style={{ display: showInput ? "initial" : "none" }}>
        <ReactS3Uploader
          getSignedUrl={getSignedUrl}
          accept={accept || "*"}
          onProgress={onProgress}
          onError={onError}
          onFinish={_onSuccess}
          contentDisposition="auto"
          uploadRequestHeaders={{}}
          inputRef={ref => setInputRef(ref)}
          capture="camera"
        />
      </div>

      {fileState === UPLOADING && (
        <div style={{ display: showProgress ? "initial" : "none" }}>
          <Progress percent={progress} status="active" />
        </div>
      )}
    </div>
  )
}

export default FileUploader
